import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { CircularProgress, Box, Backdrop, Modal, Typography, Button, TextField, Link } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ELMSType, ISynthesiaLinkRequest } from '../../services/models/LMSLinkModel';
import lmsIntegrationAgent from '../../services/api/lmsIntegrationAgent';

interface IntegrationComponentProps {
  ownerID: number;
  loadingOwnerID: boolean;
}

enum EIntegrationStatus {
  notStarted,
  pending,
  completed,
  error,
}

export default function AddSynthesiaIntegration({ ownerID, loadingOwnerID }: IntegrationComponentProps) {
  const theme = useTheme();
  const history = useHistory();
  const [apiKeyParam, setApiKeyParam] = useQueryParam('apiKey', StringParam);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKey, setApiKey] = useState('');

  const [integrationStatus, setIntegrationStatus] = useState<EIntegrationStatus>(EIntegrationStatus.notStarted);
  const [hasRun, setHasRun] = useState(false);

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiKeyInput(event.target.value);
  };

  const handleSubmitApiKey = () => {
    setApiKey(apiKeyInput.trim());
    setIntegrationStatus(EIntegrationStatus.pending);
  };

  //reset query params on unmount
  useEffect(() => {
    return () => {
      setApiKeyParam(undefined);
    };
  }, []);

  useEffect(() => {
    if (apiKeyParam && apiKeyParam !== '') {
      const sanitizedKey = apiKeyParam.trim();
      if (sanitizedKey !== '') {
        setApiKey(sanitizedKey);
        setIntegrationStatus(EIntegrationStatus.pending);
      }
    }
  }, [apiKeyParam]);

  // Handle submission when we have both owner ID and URL params
  useEffect(() => {
    if (apiKey !== '' && ownerID && !loadingOwnerID) {
      handleNewIntegration();
    }
  }, [apiKey, ownerID, loadingOwnerID]);

  const handleNewIntegration = async () => {
    if (hasRun) return;
    if (!ownerID || apiKey === '') {
      return;
    }
    setHasRun(true);

    try {
      const linkData: ISynthesiaLinkRequest = {
        identifier: 'https://api.synthesia.io/v2/',
        accessToken: apiKey,
        creatorOwnerId: ownerID,
        lmsTypeID: ELMSType.Synthesia,
      };

      const response = await lmsIntegrationAgent.CourseLMSLink.AddLMSIntegration(linkData);

      if (response.success === true || response.success === undefined || response.id) {
        setIntegrationStatus(EIntegrationStatus.completed);

        setTimeout(() => {
          history.push('/integrations?tab=Active%20Integrations');
        }, 2000);
      } else {
        setIntegrationStatus(EIntegrationStatus.error);
      }
    } catch (error) {
      console.error('Error adding Synthesia integration:', error);
      setIntegrationStatus(EIntegrationStatus.error);
    } finally {
      setHasRun(false);
    }
  };

  return (
    <>
      {!apiKey ? (
        <>
          <Typography variant='body2' color='textSecondary'>
            To use the Synthesia API, you need to be on the Synthesia &quot;Creator&quot; plan. If you&apos;re not on it yet,{' '}
            <Link href='https://www.synthesia.io/?via=mentorkit' target='_blank' rel='noopener noreferrer' color='primary' underline='hover'>
              sign up here
            </Link>
            . Then paste your Synthesia API key below. We will store a secure connection to your workspace using the canonical Synthesia API endpoint.
          </Typography>
          <TextField label={'Synthesia API Key'} variant='outlined' value={apiKeyInput} onChange={handleApiKeyChange} placeholder={'sk_live_XXXX'} />

          <Button variant='contained' color='primary' component='a' style={{ marginTop: theme.spacing(1) }} onClick={handleSubmitApiKey} disabled={!apiKeyInput.trim()}>
            {'Connect Synthesia'}
          </Button>
        </>
      ) : (
        <div style={{ marginTop: theme.spacing(3), padding: theme.spacing(2), background: theme.palette.background.paper, borderRadius: theme.spacing(1) }}>
          <Typography variant='h6' gutterBottom>
            Synthesia Credentials Received:
          </Typography>
          <Typography variant='body1'>
            <strong>API Key:</strong> {apiKey}
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: theme.spacing(1) }}>
            Processing your integration...
          </Typography>
        </div>
      )}
      {/* Processing Modal */}
      <Modal
        open={integrationStatus !== EIntegrationStatus.notStarted}
        onClose={() => {
          // Only allow closing if status is error AND user clicked outside
          if (integrationStatus === EIntegrationStatus.error) {
            setIntegrationStatus(EIntegrationStatus.notStarted);
            setApiKey('');
            setApiKeyParam(undefined);
          }
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: 'none',
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {
            {
              [EIntegrationStatus.notStarted]: <>Integration has not started...</>,
              [EIntegrationStatus.pending]: (
                <>
                  <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.text.primary }}>
                    Setting up your integration
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Wrapping things up...
                  </Typography>
                </>
              ),
              [EIntegrationStatus.completed]: (
                <>
                  <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success?.main || '#4caf50', mb: 3 }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.success?.main || '#4caf50' }}>
                    Integration complete!
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Synthesia has been successfully connected. Redirecting you now...
                  </Typography>
                </>
              ),
              [EIntegrationStatus.error]: (
                <>
                  <ErrorOutlineIcon sx={{ fontSize: 60, color: theme.palette.error?.main, mb: 3 }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.error?.main }}>
                    An error occurred
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Unable to create the connection. Try again or contact support.
                  </Typography>
                </>
              ),
            }[integrationStatus]
          }
        </Box>
      </Modal>
    </>
  );
}
